/**
 * Current Scan = the current materialized, unsigned, reviewable Safety Scan.
 * Never latest signed, scheduled run, OM window, or a fabricated placeholder.
 */

import type { ScheduledScanStatusRow } from './workspaceScheduledScanStatus';
import {
  compactCurrentScanEmptyReason,
  NO_CURRENT_SCAN_LABEL,
  resolveScheduledRunScanOutcome,
  type ScheduledRunScanOutcome,
} from './scheduledRunScanOutcome';

export type CurrentScanTruth = {
  label: string;
  empty: boolean;
  emptyReason: string | null;
  outcome: ScheduledRunScanOutcome | null;
};

export function workflowCameraId(workflowId?: string | null): string {
  const parts = String(workflowId || '').split('|').map((part) => part.trim()).filter(Boolean);
  return parts.length >= 3 ? parts[2] : '';
}

function firstScopeToken(value: unknown): string {
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = String(item || '').trim();
      if (text) return text;
    }
    return '';
  }
  return String(value || '').trim();
}

function reconstructedScopeWorkflowId(scope?: Record<string, unknown> | null): string {
  const payload = scope || {};
  const incidentType = firstScopeToken(payload.incident_type) || firstScopeToken(payload.policy_type);
  const zone = firstScopeToken(payload.zone_id) || firstScopeToken(payload.zone_ids);
  const camera = firstScopeToken(payload.camera_ids) || firstScopeToken(payload.camera_id);
  const device = firstScopeToken(payload.device_id);
  if (!incidentType || !zone || !camera || !device) return '';
  return `${incidentType}|${zone}|${camera}|${device}`;
}

/** Prefer explicit bind; else reconstruct `{incident_type}|{zone}|{camera}|{device}`. */
export function scheduledDefinitionWorkflowId(definition?: {
  scope?: Record<string, unknown> | null;
  engine_config?: Record<string, unknown> | null;
} | null): string {
  const engine = (definition?.engine_config || {}) as Record<string, unknown>;
  const context = (engine.workflow_context || {}) as Record<string, unknown>;
  const source = String(context.source_workflow_id || '').trim();
  if (source) return source;
  return reconstructedScopeWorkflowId(definition?.scope);
}

function normalizeWorkflowId(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

export function matchScheduledStatusToWorkflow(
  rows: ScheduledScanStatusRow[],
  workflowId?: string | null,
): ScheduledScanStatusRow | null {
  const wanted = normalizeWorkflowId(workflowId);
  if (!wanted) return null;
  return rows.find((row) => normalizeWorkflowId(row.workflowId) === wanted) || null;
}

export function resolveCurrentScanTruth(args: {
  currentScanLabel?: string | null;
  currentScanWorkflowState?: string | null;
  hasMaterializedUnsignedScan?: boolean;
  scheduledRow?: ScheduledScanStatusRow | null;
}): CurrentScanTruth {
  const hasCurrent = Boolean(args.hasMaterializedUnsignedScan);
  const label = hasCurrent
    ? (String(args.currentScanLabel || args.currentScanWorkflowState || '').trim() || 'Current Safety Scan')
    : NO_CURRENT_SCAN_LABEL;
  if (hasCurrent) {
    return { label, empty: false, emptyReason: null, outcome: null };
  }
  const scheduled = args.scheduledRow || null;
  const outcome = scheduled
    ? resolveScheduledRunScanOutcome({
      id: scheduled.runId,
      status: scheduled.rawStatus,
      terminal_reason_code: scheduled.terminalReasonCode,
      terminal_reason_message: scheduled.terminalReasonMessage,
      links: {
        result_summary_id: scheduled.resultSummaryId,
        report_id: scheduled.reportId,
      },
    })
    : null;
  return {
    label: NO_CURRENT_SCAN_LABEL,
    empty: true,
    emptyReason: outcome
      ? compactCurrentScanEmptyReason(outcome, scheduled?.nextOccurrenceLabel)
      : null,
    outcome,
  };
}
